import { Container, Grid } from '@mui/material';
import PageHeader from '../../../components/PageHeader';
import PageHeadline from '../../../components/PageHeadline';
import { useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import Command from '../../../action/command';
import Events from '../../../action/event';
import { HeaderRightButtonContext } from '../../../layout/MainLayout';
import { paths } from '../../../routes';
import {
    assetListSelector,
    issueReportCreateFailedSelector,
    issueReportCreatedSelector,
} from '../../../selector/issueReportsSelector';
import IssueReportLoaderOverlay from '../../issueReports/IssueReportLoaderOverlay';
import SelectField from '../../../components/ui/SelectField';
import InputField from '../../../components/ui/InputField';
import TextareaField from '../../../components/ui/TextareaField';
import IssueReportsCommands from '../../../action/issueReportsCommands';
import CustomButton from '../../../components/CustomButton';
import { useAuth0 } from '../../../react-auth0-spa';
import { RootState } from '../../../store/store';
import { issueReportPickerLabels } from './issueReportPickerLabels';
import i18n from 'i18next';
import { roomNounOf, serviceTypeLabel } from '../../../utils/useCaseLabels';

interface IssueReportDialogProps {}

interface LocationState {
    assetId?: string;
    orderId?: string;
}

interface IssueReportApartmentRentalCreationPayload {
    assetId: string;
    message: string;
    issueType: string;
    bookingNumber: string;
    service: string;
}


const ApartmentRentalAddIssueReportPage = (props: IssueReportDialogProps) => {
    const { state } = useLocation() as { state: LocationState };
    const location = useLocation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { user } = useAuth0();
    const issueReportCreated = useSelector(issueReportCreatedSelector);
    const issueReportCreateFailed = useSelector(issueReportCreateFailedSelector);
    const [loading, setLoading] = useState<boolean>(false);
    const { setButtonState } = useContext(HeaderRightButtonContext);
    const assets = useSelector(assetListSelector);
    const [issueReport, setIssueReport] = useState<IssueReportApartmentRentalCreationPayload>({
        assetId: state?.assetId || '',
        message: '',
        issueType: '',
        bookingNumber: '',
        service: '',
    });
    const [apartmentAddress, setApartmentAddress] = useState<string>('');
    const allowedServices = useSelector((state: RootState) => state.authentication.config?.services || []);

    const serviceKeys = ['general', ...allowedServices.filter((s: string) => s !== 'general')];
    const serviceOptions = serviceKeys.map((key: string) => ({
        key,
        // Every service's name, hall included, from service_type_selector.<service>.
        label: serviceTypeLabel(t, key),
    }));

    const currentLanguage = i18n.language;


    const isSubmitDisabled = Object.entries(issueReport)
        .filter(([key]) => key !== 'bookingNumber')
        .some(([, value]) => value === '' || value === null);

    const filteredAssets = useMemo(() => {
        if (!issueReport.service) return assets;
        return assets.filter(asset => asset.compartmentTypes.includes(issueReport.service));
    }, [issueReport.service, assets]);

    const labelById = useMemo(
        () => issueReportPickerLabels(filteredAssets, currentLanguage as 'en' | 'de'),
        [filteredAssets, currentLanguage],
    );

    useEffect(() => {
        if(!issueReport.assetId) return;
        const stillValid = filteredAssets.some(a => a.id === issueReport.assetId);
        if (!stillValid) {
            setIssueReport(prev => ({ ...prev, assetId: '' }));
            setApartmentAddress('');
        }
    }, [ filteredAssets, issueReport.assetId]);
    const submitDamageReport = () => {
        setLoading(true);
        dispatch(
            Command.IssueReport.createIssueReport({
                message: issueReport.message,
                assets: [
                    {
                        assetId: issueReport.assetId,
                        depotId: '',
                        door: 0,
                    },
                ],
                issueType: issueReport.issueType,
                service: issueReport.service,
                orderId: state?.orderId || '',
                bookingNumber: issueReport.bookingNumber,
                profileInfo: {
                    name: user.name,
                    sub: user.sub,
                    nickname: user.nickname,
                    email: user.email,
                    picture: user.picture,
                },
            }),
        );
    };

    const updateHeaderRightButton = (status: boolean, route: string) => {
        setButtonState({
            isCancelButton: status,
            previousRoute: route,
        });
    };

    const issueTypeOptions = [
        'furniture_fixtures',
        'bathroom_sanitary',
        'kitchen_appliances',
        'hvac',
        'electrical_systems',
        'plumbing_issues',
        'internet_connectivity',
        'cleaning_housekeeping',
        'pest_control',
        'safety_security',
        'exterior_outdoor',
        'noise_neighbor',
        'problem_with_app',
        'other_miscellaneous',
    ].map((key) => ({
        text: t(`issue_type.${key}`),
        value: key,
    }));

    // One effect for both outcomes, and one `setLoading` writer — see the
    // twin of this effect in `pages/issueReports/AddIssueReportPage.tsx`.
    useEffect(() => {
        if (!issueReportCreated && !issueReportCreateFailed) {
            return;
        }
        setLoading(false);
        const failed = issueReportCreateFailed;
        dispatch(Events.IssueReport.createdIssueReport({ success: false }));
        if (failed) {
            // Stay on the form with the input intact; the error snackbar says
            // nothing was saved. Navigating away made a failed create look
            // like a successful one.
            return;
        }
        updateHeaderRightButton(false, '');
        navigate(-1);
    }, [issueReportCreated, issueReportCreateFailed]);

    useLayoutEffect(() => {
        if (state) {
            const route = location.pathname;
            const parts = route.split('/').slice(0, 3);
            updateHeaderRightButton(true, parts.join('/'));
        } else {
            updateHeaderRightButton(true, paths.issueReports);
        }
    }, []);

    useEffect(() => {
        if (issueReport.assetId) {
            const asset = assets.find((asset) => asset.id === issueReport.assetId);
            if (asset) {
                setApartmentAddress(asset.address);
            }
        }
    }, [issueReport.assetId]);

    useLayoutEffect(() => {
        dispatch(IssueReportsCommands.fetchAssetsForIssueReports({}));
    }, []);

    return (
        <>
            <Container maxWidth={false}>
                <div className={'py-6'}>
                    <PageHeader>
                        <PageHeadline>{t('issue_reports.issue_report')}</PageHeadline>
                    </PageHeader>
                </div>
                <div className={'bg-white rounded-lg shadow-sm p-5'}>
                    <Grid
                        container
                        rowSpacing={3}
                        columnSpacing={{ xs: 0, sm: 2 }}
                        sx={{
                            width: '100%',
                            justifyContent: 'flex-start',
                            alignItems: 'flex-start',
                            display: 'flex',
                        }}
                    >
                        <Grid item xs={12} sm={6}>
                            <SelectField
                                options={filteredAssets.map(asset => labelById.get(asset.id) ?? '')}
                                value={labelById.get(issueReport.assetId) ?? ''}
                                onChange={(selectedName) => {
                                    const selected = filteredAssets.find(
                                        (asset) => labelById.get(asset.id) === selectedName,
                                    );
                                    if (selected) {
                                        setIssueReport(prev => ({ ...prev, assetId: selected.id }));
                                        setApartmentAddress(selected.address ?? '');
                                    }
                                }}
                                label={t('issue_report.select_apartment_name')}
                                noOptions={t(`issue_report.no_${roomNounOf(issueReport.service)}s`)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <SelectField
                                options={issueTypeOptions.map((opt) => opt.text)}
                                value={issueTypeOptions.find((opt) => opt.value === issueReport.issueType)?.text ?? ''}
                                onChange={(selectedText) => {
                                    const selected = issueTypeOptions.find((opt) => opt.text === selectedText);
                                    if (selected) {
                                        setIssueReport({ ...issueReport, issueType: selected.value });
                                    }
                                }}
                                label={t('issue_report.issue_type')}
                                noOptions={t('issue_report.no_issue_types')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InputField
                                label={t('issue_report.select_apartment_address')}
                                value={apartmentAddress}
                                disabled={true}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <InputField
                                label={t('issue_report.booking_id')}
                                value={issueReport.bookingNumber}
                                onChange={(value) => setIssueReport({ ...issueReport, bookingNumber: value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12}>
                            <SelectField
                                options={serviceOptions.map((opt) => opt.label)}
                                value={serviceOptions.find((opt) => opt.key === issueReport.service)?.label ?? ''}
                                onChange={(label) => {
                                    const selected = serviceOptions.find((opt) => opt.label === label);
                                    if (selected) setIssueReport((prev) => ({ ...prev, service: selected.key }));
                                }}
                                label={t('common.service')}
                                noOptions={t('issue_report.no_services')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12}>
                            <TextareaField
                                rows={5}
                                label={t('issue_report.description')}
                                value={issueReport.message}
                                onChange={(value) => setIssueReport({ ...issueReport, message: value })}
                            />
                        </Grid>

                        <Grid item xs={12} sm={12}>
                            <div className={'flex gap-2 justify-start'}>
                                <div>
                                    <CustomButton
                                        title={t('issue_report.send_issue_report')}
                                        onClick={submitDamageReport}
                                        disabled={isSubmitDisabled || loading}
                                        isFilled={true}
                                        fullWidth={false}
                                    />
                                </div>
                                <div>
                                    <CustomButton
                                        title={t('issue_report.cancel_issue_report')}
                                        onClick={() => navigate(paths.rentalIssueReports)}
                                        disabled={false}
                                        isFilled={false}
                                        fullWidth={false}
                                    />
                                </div>
                            </div>
                        </Grid>
                    </Grid>
                </div>
                <IssueReportLoaderOverlay open={loading} />
            </Container>
        </>
    );
};

export default ApartmentRentalAddIssueReportPage;
